// frontend/src/modules/payroll/components/PayrollSettingsPanel.tsx
//
// Payroll settings: payslip template config (YTD / attendance / footer /
// accent) + the NACHA company identifiers required by the ACH export.
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { VISUAL } from '../../../design-system/visual';
import { payrollApi } from '../api';
import type { PayrollSettings } from '../types';

export function PayrollSettingsPanel() {
  const settingsQ = useApi<{ settings: PayrollSettings }>('/api/payroll/settings');
  const [saving, setSaving] = React.useState(false);

  // Template config
  const [showYtd, setShowYtd] = React.useState(true);
  const [showAttendance, setShowAttendance] = React.useState(false);
  const [footerNote, setFooterNote] = React.useState('');
  const [accent, setAccent] = React.useState<'default' | 'plain'>('default');

  // ACH identifiers
  const [companyName, setCompanyName] = React.useState('');
  const [companyId, setCompanyId] = React.useState('');
  const [odfiRouting, setOdfiRouting] = React.useState('');

  React.useEffect(() => {
    const s = settingsQ.data?.settings;
    if (!s) return;
    setShowYtd(s.payslip_config.show_ytd);
    setShowAttendance(s.payslip_config.show_attendance);
    setFooterNote(s.payslip_config.footer_note);
    setAccent(s.payslip_config.accent);
    setCompanyName(s.payroll_company_name ?? '');
    setCompanyId(s.payroll_company_id ?? '');
    setOdfiRouting(s.payroll_odfi_routing ?? '');
  }, [settingsQ.data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await payrollApi.updateSettings({
        payroll_company_name: companyName.trim() || null,
        payroll_company_id: companyId.trim() || null,
        payroll_odfi_routing: odfiRouting.trim() || null,
        payslip_config: {
          show_ytd: showYtd,
          show_attendance: showAttendance,
          footer_note: footerNote,
          accent,
        },
      });
      toast.success('Payroll settings saved');
      settingsQ.reload();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={save} className="space-y-6 max-w-3xl">
      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <h3 className={[VISUAL.dashSection, 'mb-1.5'].join(' ')}>Payslip template</h3>
        <p className="text-[12.5px] text-muted-foreground mb-5">
          Controls what appears on the PDF payslips you email to employees.
        </p>
        <div className="space-y-4">
          <CheckRow
            label="Show year-to-date totals"
            caption="YTD gross, deductions, net, and per-rule tax withheld."
            checked={showYtd}
            onChange={setShowYtd}
          />
          <CheckRow
            label="Show attendance summary"
            caption="Total hours worked during the pay period."
            checked={showAttendance}
            onChange={setShowAttendance}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div className="grid gap-1.5">
              <Label>Style</Label>
              <Select value={accent} onValueChange={(v) => setAccent(v as 'default' | 'plain')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Accented headers</SelectItem>
                  <SelectItem value="plain">Plain (no shading)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Footer note <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                value={footerNote}
                onChange={(e) => setFooterNote(e.target.value)}
                maxLength={240}
                placeholder="This payslip is confidential."
              />
            </div>
          </div>
        </div>
      </section>

      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <h3 className={[VISUAL.dashSection, 'mb-1.5'].join(' ')}>Direct deposit (ACH)</h3>
        <p className="text-[12.5px] text-muted-foreground mb-5">
          Required for the NACHA bank file export on finalized runs. Your bank (the ODFI)
          provides the company ID — usually "1" followed by your EIN.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="grid gap-1.5">
            <Label>Company name</Label>
            <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} maxLength={50} placeholder="Acme Inc" />
          </div>
          <div className="grid gap-1.5">
            <Label>Company ID</Label>
            <Input value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="1123456789" />
          </div>
          <div className="grid gap-1.5">
            <Label>ODFI routing number</Label>
            <Input value={odfiRouting} onChange={(e) => setOdfiRouting(e.target.value)} placeholder="011000015" />
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving || settingsQ.loading} className="rounded-full h-11 px-6 text-[14px] font-semibold">
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </form>
  );
}

function CheckRow({
  label, caption, checked, onChange,
}: { label: string; caption: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-border accent-saffron"
      />
      <span className="flex-1">
        <span className="text-[13.5px] font-semibold text-foreground block">{label}</span>
        <span className="text-[12.5px] text-muted-foreground block mt-0.5">{caption}</span>
      </span>
    </label>
  );
}
